import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, IconButton, Card, CardContent, Avatar, Chip, Container, TextField, InputAdornment, Collapse, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CakeIcon from '@mui/icons-material/Cake';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import upscaleBackground from '../assets/upscale-background.jpeg';

// Categories defined outside component to avoid recreating on each render
const CATEGORIES = [
  { key: 'politics', label: 'Politics', color: '#667eea' },
  { key: 'education', label: 'Education', color: '#4facfe' },
  { key: 'judiciary', label: 'Judiciary', color: '#764ba2' },
  { key: 'military', label: 'Military', color: '#f093fb' },
  { key: 'award', label: 'Awards', color: '#ffd700' },
  { key: 'government', label: 'Government', color: '#30cfd0' },
  { key: 'leadership', label: 'Leadership', color: '#a8edea' },
  { key: 'birth', label: 'Birth', color: '#ff6b6b' },
  { key: 'death', label: 'Death', color: '#6c757d' },
  { key: 'health', label: 'Health', color: '#20c997' },
  { key: 'international', label: 'International', color: '#fd7e14' },
];

const getEventColor = (category) => {
  const cat = CATEGORIES.find(c => c.key === category);
  return cat ? cat.color : '#f093fb';
};

const getEventIcon = (category) => {
  switch(category) {
    case 'politics': return <GavelIcon />;
    case 'education': return <SchoolIcon />;
    case 'judiciary': return <AccountBalanceIcon />;
    case 'military': return <MilitaryTechIcon />;
    case 'award': return <EmojiEventsIcon />;
    case 'government': return <BusinessIcon />;
    case 'leadership': return <PeopleIcon />;
    case 'birth': return <CakeIcon />;
    case 'death': return <FavoriteIcon />;
    case 'health': return <LocalHospitalIcon />;
    case 'international': return <PublicIcon />;
    default: return <WorkIcon />;
  }
};

const getCategoryWithIcon = (cat) => {
  return { ...cat, icon: getEventIcon(cat.key) };
};

const TimelinePage = ({ data, isMobile, onBack, onPersonClick, selectedPerson, extraEvents = [] }) => {
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [collapsedDecades, setCollapsedDecades] = useState({});

  const categories = CATEGORIES.map(getCategoryWithIcon);

  useEffect(() => {
    const events = getMwendwaKitaviHistoricalEvents().map(event => ({
      type: 'historical',
      person: { name: event.personName, role: event.role || 'Family Member', generation: event.generation },
      date: new Date(event.date),
      title: event.title,
      description: event.description,
      icon: getEventIcon(event.category),
      color: getEventColor(event.category),
      priority: event.priority || 4,
      category: event.category,
      details: event.details,
      isHistorical: true,
    }));

    // Weave in approved member-submitted stories (living history)
    const storyEvents = (extraEvents || []).map(story => ({
      type: 'story',
      person: {
        name: story.personName || 'Our Family',
        role: story.submittedBy?.name ? `Shared by ${story.submittedBy.name}` : 'Family Member',
        generation: null,
      },
      date: new Date(story.date),
      title: story.title,
      description: story.description,
      icon: getEventIcon(story.category),
      color: getEventColor(story.category),
      priority: 3,
      category: story.category,
      details: story.details,
      isHistorical: false,
    }));

    const merged = [...events, ...storyEvents];
    merged.sort((a, b) => a.date - b.date);
    setTimelineEvents(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, extraEvents]);

  const getMwendwaKitaviHistoricalEvents = () => {
    return [
      // GENERATION 1 - Chief Mwendwa Kitavi
      {
        personName: 'Mwendwa Kitavi',
        date: '1874-01-01',
        title: 'Birth of Chief Mwendwa Kitavi',
        description: 'Founding patriarch of the Mwendwa Kitavi family',
        category: 'birth',
        details: 'Born in Kitui, Kenya - the beginning of a remarkable family legacy',
        generation: 1,
        role: 'Founding Patriarch',
        priority: 5
      },
      {
        personName: 'Mwendwa Kitavi',
        date: '1914-01-01',
        title: 'Chief Mwendwa Kitavi became a colonial home guard',
        description: 'Served during colonial period in Kenya',
        category: 'military',
        details: 'Colonial home guard service during World War I era',
        generation: 1,
        role: 'Colonial Home Guard'
      },
      {
        personName: 'Mwendwa Kitavi',
        date: '1918-01-01',
        title: 'Chief Mwendwa Kitavi appointed Paramount Chief',
        description: 'Appointed by British royal administration as Paramount Chief',
        category: 'leadership',
        details: 'Paramount Chief of the Kitui region - highest traditional authority',
        generation: 1,
        role: 'Paramount Chief',
        priority: 5
      },
      {
        personName: 'Mwendwa Kitavi',
        date: '1964-01-01',
        title: 'Death of Chief Mwendwa Kitavi',
        description: 'The founding patriarch passed away at approximately 90 years of age, leaving a lasting legacy',
        category: 'death',
        details: 'His legacy lives on through his descendants who continue to serve Kenya',
        generation: 1,
        role: 'Founding Patriarch'
      },

      // GENERATION 2 - Children of Mwendwa Kitavi
      {
        personName: 'Ngala Mwendwa',
        date: '1922-01-01',
        title: 'Birth of Ngala Mwendwa',
        description: 'Son of Chief Mwendwa Kitavi',
        category: 'birth',
        details: 'Would become a prominent politician and Cabinet Minister',
        generation: 2,
        role: 'Future Politician'
      },
      {
        personName: 'Ngala Mwendwa',
        date: '1961-01-01',
        title: 'Ngala Mwendwa elected to Legislative Council',
        description: 'Representative for Kitui Central in pre-independence Kenya',
        category: 'politics',
        details: "Member of Kenya's Legislative Council (1961-1963) - helped shape independent Kenya",
        generation: 2,
        role: 'Legislative Council Member',
        priority: 5
      },
      {
        personName: 'Ngala Mwendwa',
        date: '1963-12-12',
        title: 'Kenya Independence - Ngala Mwendwa becomes MP & Minister',
        description: 'MP for Kitui and Minister for Labour and Social Services',
        category: 'government',
        details: 'First Cabinet position at Kenya\'s independence - Minister for Labour and Social Services (1963-1969)',
        generation: 2,
        role: 'Cabinet Minister',
        priority: 5
      },
      {
        personName: 'Ngala Mwendwa',
        date: '1969-01-01',
        title: 'Ngala Mwendwa re-elected MP & continued as Minister',
        description: 'MP for Kitui Central Constituency and Cabinet Minister',
        category: 'government',
        details: 'Served as MP for Kitui Central (1969-1974) and Minister for Labour',
        generation: 2,
        role: 'Cabinet Minister'
      },
      {
        personName: 'Kitili Mwendwa',
        date: '1929-01-01',
        title: 'Birth of Kitili Mwendwa',
        description: 'Son of Chief Mwendwa Kitavi',
        category: 'birth',
        details: 'Would become the first African Chief Justice of Kenya',
        generation: 2,
        role: 'Future Chief Justice'
      },
      {
        personName: 'Kitili Mwendwa',
        date: '1968-01-01',
        title: 'Kitili Mwendwa became First African Chief Justice of Kenya',
        description: 'Historic appointment as first African Chief Justice',
        category: 'judiciary',
        details: 'Served as Chief Justice from 1968-1971, youngest ever at age 39 - a historic milestone',
        generation: 2,
        role: 'Chief Justice of Kenya',
        priority: 5
      },
      {
        personName: 'Kitili Mwendwa',
        date: '1983-01-01',
        title: 'Kitili Mwendwa elected MP for Kitui West',
        description: 'Entered politics after distinguished judicial career',
        category: 'politics',
        details: 'Elected in a by-election after court petition - transitioned from judiciary to politics',
        generation: 2,
        role: 'Member of Parliament'
      },
      {
        personName: 'Kitili Mwendwa',
        date: '1985-01-01',
        title: 'Death of Kitili Mwendwa',
        description: 'Kenya\'s first African Chief Justice passed away',
        category: 'death',
        details: 'Left an indelible mark on Kenya\'s judicial history',
        generation: 2,
        role: 'Former Chief Justice'
      },
      {
        personName: 'Kyale Mwendwa',
        date: '1927-01-01',
        title: 'Birth of Kyale Mwendwa',
        description: 'Son of Chief Mwendwa Kitavi',
        category: 'birth',
        details: 'Would become first African Director of Education and Cabinet Minister',
        generation: 2,
        role: 'Future Educator & Politician'
      },
      {
        personName: 'Kyale Mwendwa',
        date: '1960-01-01',
        title: 'Kyale Mwendwa became First African Director of Education',
        description: 'Pioneer educationist in Kenya',
        category: 'education',
        details: 'First African to head Kenya\'s education system - shaped modern Kenyan education',
        generation: 2,
        role: 'Director of Education',
        priority: 5
      },
      {
        personName: 'Kyale Mwendwa',
        date: '1985-01-01',
        title: 'Kyale Mwendwa elected MP for Kitui West',
        description: 'Member of Parliament and Cabinet Minister',
        category: 'politics',
        details: "Won by-election after brother Kitili's death, served as Minister for Livestock Development and Water",
        generation: 2,
        role: 'Cabinet Minister'
      },
      {
        personName: 'Nyiva Mwendwa',
        date: '1974-01-01',
        title: 'Nyiva Mwendwa elected MP for Kitui West',
        description: 'First term as Member of Parliament',
        category: 'politics',
        details: 'Began political career representing Kitui West - a trailblazer for women in politics',
        generation: 2,
        role: 'Member of Parliament'
      },
      {
        personName: 'Nyiva Mwendwa',
        date: '1995-01-01',
        title: "Nyiva Mwendwa became Kenya's First Female Cabinet Minister",
        description: 'Minister for Culture and Social Services',
        category: 'government',
        details: 'Appointed by President Moi - historic achievement as first woman Cabinet Minister in Kenya',
        generation: 2,
        role: 'Cabinet Minister',
        priority: 5
      },
      {
        personName: 'Nyiva Mwendwa',
        date: '2013-01-01',
        title: 'Nyiva Mwendwa elected First Kitui County Woman Representative',
        description: 'County Woman Representative under new constitution',
        category: 'politics',
        details: 'Served from 2013-2017 under devolved government system - continuing her service',
        generation: 2,
        role: 'County Woman Representative'
      },

      // GENERATION 3 - Grandchildren
      {
        personName: 'Musyoka Masai',
        date: '1932-01-01',
        title: 'Birth of Musyoka Masai',
        description: 'Grandchild of Chief Mwendwa Kitavi',
        category: 'birth',
        details: 'Would become Head of Public Health Kenya',
        generation: 3,
        role: 'Future Health Leader'
      },
      {
        personName: 'Musyoka Masai',
        date: '1985-01-01',
        title: 'Musyoka Masai became Head of Public Health Kenya',
        description: 'National public health leadership',
        category: 'health',
        details: 'Led public health initiatives nationally - continuing the family tradition of public service',
        generation: 3,
        role: 'Head of Public Health'
      },
      {
        personName: 'Musyoka Masai',
        date: '2010-01-01',
        title: 'Musyoka Masai received Head of State Commendation Award',
        description: 'National recognition for public service',
        category: 'award',
        details: 'Honored by the President for outstanding contributions to public health',
        generation: 3,
        role: 'Award Recipient',
        priority: 4
      },
      {
        personName: 'Leonard Kathukya Munguti',
        date: '1950-01-01',
        title: 'Birth of Leonard Kathukya Munguti',
        description: 'Grandchild of Chief Mwendwa Kitavi',
        category: 'birth',
        details: 'Would serve in local government leadership',
        generation: 3,
        role: 'Future Local Leader'
      },
      {
        personName: 'Leonard Kathukya Munguti',
        date: '1997-01-01',
        title: 'Leonard Kathukya Munguti became Nominated Councillor',
        description: 'Matinyani Ward representative',
        category: 'politics',
        details: 'Began political career as nominated councillor for Matinyani Ward',
        generation: 3,
        role: 'Councillor'
      },
      {
        personName: 'Leonard Kathukya Munguti',
        date: '2002-01-01',
        title: 'Leonard Kathukya Munguti elected Councillor',
        description: 'Matinyani Ward elected representative',
        category: 'politics',
        details: 'Elected councillor for Matinyani Ward - gaining voter mandate',
        generation: 3,
        role: 'Elected Councillor'
      },
      {
        personName: 'Leonard Kathukya Munguti',
        date: '2005-01-01',
        title: 'Leonard Kathukya Munguti appointed Deputy Mayor',
        description: 'Kitui Municipality leadership',
        category: 'government',
        details: 'Served as Deputy Mayor of Kitui Municipality - rising in local government',
        generation: 3,
        role: 'Deputy Mayor'
      },
      {
        personName: 'Mbiti Kasee',
        date: '1926-01-01',
        title: 'Birth of Mbiti Kasee',
        description: 'Family member',
        category: 'birth',
        details: 'Would become Chief of Matinyani Location',
        generation: 3,
        role: 'Future Chief'
      },
      {
        personName: 'Mbiti Kasee',
        date: '1972-01-01',
        title: 'Mbiti Kasee became Chief of Matinyani Location',
        description: 'Traditional community leadership',
        category: 'leadership',
        details: 'Traditional leadership role in Matinyani - continuing the family\'s legacy of chieftainship',
        generation: 3,
        role: 'Chief'
      },
      {
        personName: 'Kennedy Ngumbau Mulwa',
        date: '1965-01-01',
        title: 'Birth of Kennedy Ngumbau Mulwa',
        description: 'Family member',
        category: 'birth',
        details: 'Would become Chairman of Mumias Sugar Company',
        generation: 3,
        role: 'Future Business Leader'
      },
      {
        personName: 'Kennedy Ngumbau Mulwa',
        date: '2017-01-01',
        title: 'Kennedy Ngumbau Mulwa appointed Chairman of Mumias Sugar Company',
        description: 'Leadership in major Kenyan corporation',
        category: 'leadership',
        details: 'Appointed as Chairman of Mumias Sugar Company - one of Kenya\'s largest sugar producers',
        generation: 3,
        role: 'Corporate Chairman'
      },

      // KEY NATIONAL EVENTS
      {
        personName: 'Mwendwa Family',
        date: '1963-12-12',
        title: 'Kenya Independence Day',
        description: 'Kenya gains independence from British colonial rule',
        category: 'international',
        details: 'Multiple Mwendwa family members played key roles in the new independent government',
        generation: 0,
        role: 'National Event',
        priority: 5
      },
      {
        personName: 'Mwendwa Family',
        date: '2010-08-27',
        title: 'New Kenyan Constitution Promulgated',
        description: 'Kenya adopts new constitution with devolved government',
        category: 'international',
        details: 'Created new political opportunities including County Woman Representative positions',
        generation: 0,
        role: 'National Event',
        priority: 4
      },

      // FAMILY ASSOCIATION LEADERSHIP
      {
        personName: 'Mwendwa Kitavi Family Association',
        date: '2025-12-13',
        title: 'Family Association Leadership Elected',
        description: 'New office bearers elected to lead the Mwendwa Kitavi Family Association',
        category: 'leadership',
        details: 'President: Kali Mulwa • Vice President: Mumo Mwendwa • Chairman: Kavili Mbiti • Secretary: Kasee Mulinge • Treasurer: Kasyoka Kaveva • Youth Leader: Makau Nguthu',
        generation: 0,
        role: 'Family Association',
        priority: 5
      },
    ];
  };

  // Filter events based on search and category
  const filteredEvents = useMemo(() => {
    return timelineEvents.filter(event => {
      const matchesSearch = searchTerm === '' || 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategories.length === 0 || 
        selectedCategories.includes(event.category);
      
      return matchesSearch && matchesCategory;
    });
  }, [timelineEvents, searchTerm, selectedCategories]);

  // Calculate statistics
  const stats = useMemo(() => {
    const categoryCount = {};
    const generationCount = {};
    const uniquePeople = new Set();
    
    timelineEvents.forEach(event => {
      categoryCount[event.category] = (categoryCount[event.category] || 0) + 1;
      if (event.person.generation) {
        generationCount[event.person.generation] = (generationCount[event.person.generation] || 0) + 1;
      }
      if (event.person.name !== 'Mwendwa Family') {
        uniquePeople.add(event.person.name);
      }
    });
    
    return {
      totalEvents: timelineEvents.length,
      categoryCount,
      generationCount,
      uniquePeople: uniquePeople.size,
      timeSpan: timelineEvents.length > 0 ? 
        `${timelineEvents[0]?.date?.getFullYear()} - ${timelineEvents[timelineEvents.length - 1]?.date?.getFullYear()}` : 'N/A'
    };
  }, [timelineEvents]);

  const groupByDecade = (events) => {
    const decades = {};
    events.forEach(event => {
      if (!event.date) return;
      const decade = Math.floor(event.date.getFullYear() / 10) * 10;
      if (!decades[decade]) decades[decade] = [];
      decades[decade].push(event);
    });
    return decades;
  };

  const decadeGroups = groupByDecade(filteredEvents);

  const getDisplayDate = (event) => {
    if (event.date.getDate() === 1 && event.date.getMonth() === 0 && event.date.getFullYear() > 1000) {
      return event.date.getFullYear().toString();
    }
    return event.date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handlePersonClick = (person) => {
    if (person && person._id && onPersonClick) {
      onPersonClick(person);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleDecade = (decade) => {
    setCollapsedDecades(prev => ({
      ...prev,
      [decade]: !prev[decade]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const getGenerationLabel = (gen) => {
    switch(gen) {
      case 1: return '1st Gen';
      case 2: return '2nd Gen';
      case 3: return '3rd Gen';
      case 4: return '4th Gen';
      default: return '';
    }
  };

  const getGenerationColor = (gen) => {
    switch(gen) {
      case 1: return '#00FFFF';
      case 2: return '#98FB98';
      case 3: return '#FFFACD';
      case 4: return '#FFE4B5';
      default: return '#E6E6FA';
    }
  };

  return (
    <Box style={{ 
      minHeight: '100vh', 
      backgroundImage: `url(${upscaleBackground})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      position: 'relative',
      paddingBottom: '40px'
    }}>
      {/* Dark overlay for better readability */}
      <Box style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(20, 20, 40, 0.7) 100%)',
        zIndex: 0
      }} />
      
      {/* Print Styles */}
      <style>
        {`
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-print { display: none !important; }
            .print-break { page-break-after: always; }
          }
        `}
      </style>
      
      {/* Content - positioned above the overlay */}
      <Box style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box className="no-print" style={{ 
          background: 'rgba(255, 255, 255, 0.98)', 
          boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
          borderBottom: '3px solid #667eea',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <Container maxWidth="lg">
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={handleBack} style={{ marginRight: '12px' }}>
                  <ArrowBackIcon style={{ color: '#333' }} />
                </IconButton>
                <Box>
                  <Typography variant="h6" style={{ fontWeight: 'bold', color: '#333', lineHeight: 1.2 }}>
                    Mwendwa Kitavi Family Timeline
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666' }}>
                    {stats.timeSpan} • {stats.uniquePeople} Family Members • {stats.totalEvents} Events
                  </Typography>
                </Box>
              </Box>
              <Box style={{ display: 'flex', gap: '8px' }}>
                <Tooltip title="Filter Events">
                  <IconButton onClick={() => setShowFilters(!showFilters)} style={{ 
                    backgroundColor: showFilters ? '#667eea' : 'transparent',
                    color: showFilters ? '#fff' : '#333'
                  }}>
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print Timeline">
                  <IconButton onClick={handlePrint}>
                    <PrintIcon style={{ color: '#333' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Search & Filters */}
            <Collapse in={showFilters}>
              <Box style={{ paddingBottom: '16px' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search events, people..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                  style={{ marginBottom: '12px' }}
                />
                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {categories.map(cat => (
                    <Chip
                      key={cat.key}
                      label={cat.label}
                      size="small"
                      icon={React.cloneElement(cat.icon, { style: { fontSize: '14px' } })}
                      onClick={() => toggleCategory(cat.key)}
                      style={{
                        backgroundColor: selectedCategories.includes(cat.key) ? cat.color : 'rgba(0,0,0,0.08)',
                        color: selectedCategories.includes(cat.key) ? '#fff' : '#666',
                        fontWeight: selectedCategories.includes(cat.key) ? 'bold' : 'normal',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    />
                  ))}
                  {selectedCategories.length > 0 && (
                    <Chip
                      label="Clear All"
                      size="small"
                      onClick={() => setSelectedCategories([])}
                      style={{ backgroundColor: '#ff6b6b', color: '#fff', cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Box>
            </Collapse>
          </Container>
        </Box>

        {/* Content */}
        <Container maxWidth="lg" style={{ paddingTop: '30px' }}>
          {/* Stats Summary */}
          <Box style={{ 
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '16px',
            marginBottom: '30px'
          }}>
            {[
              { label: 'Total Events', value: filteredEvents.length, color: '#667eea' },
              { label: 'Family Members', value: stats.uniquePeople, color: '#4facfe' },
              { label: 'Generations', value: Object.keys(stats.generationCount).length, color: '#f093fb' },
              { label: 'Categories', value: Object.keys(stats.categoryCount).length, color: '#30cfd0' },
            ].map((stat, idx) => (
              <Box key={idx} style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${stat.color}`
              }}>
                <Typography variant="h4" style={{ fontWeight: 'bold', color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" style={{ color: '#666' }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Intro Box */}
          <Box style={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            borderRadius: '12px', 
            padding: '24px', 
            marginBottom: '40px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '2px solid #667eea',
            backdropFilter: 'blur(10px)'
          }}>
            <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '16px', color: '#333' }}>
              🏛️ Family Legacy Timeline
            </Typography>
            <Typography variant="body1" style={{ color: '#666', lineHeight: 1.8 }}>
              This timeline chronicles the remarkable journey of the <strong>Mwendwa Kitavi family</strong> from Kitui, 
              showcasing their significant contributions to Kenyan public service and politics spanning 
              over a century. From <strong>Chief Mwendwa Kitavi's</strong> appointment as Paramount Chief in 1918, 
              to <strong>Kitili Mwendwa</strong> becoming Kenya's first African Chief Justice, to <strong>Nyiva Mwendwa</strong> breaking 
              barriers as Kenya's first female Cabinet Minister - this family has shaped Kenya's history.
            </Typography>
          </Box>

          {/* Timeline */}
          <Box style={{ position: 'relative' }}>
            {/* Vertical Line */}
            <Box
              style={{
                position: 'absolute',
                left: isMobile ? '20px' : '50%',
                transform: isMobile ? 'none' : 'translateX(-50%)',
                top: 0,
                bottom: 0,
                width: '3px',
                background: 'linear-gradient(180deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                zIndex: 0,
                borderRadius: '3px'
              }}
            />

            {Object.keys(decadeGroups).sort().map((decade) => (
              <Box key={decade} style={{ marginBottom: isMobile ? '30px' : '40px', position: 'relative' }}>
                {/* Decade Badge */}
                <Box 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    marginBottom: isMobile ? '20px' : '30px',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleDecade(decade)}
                >
                  <Chip
                    label={
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{decade}s</span>
                        <span style={{ fontSize: '12px', opacity: 0.8 }}>
                          ({decadeGroups[decade].length} events)
                        </span>
                        {collapsedDecades[decade] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                      </Box>
                    }
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '14px' : '18px',
                      padding: isMobile ? '8px 16px' : '12px 24px',
                      height: 'auto',
                      boxShadow: '0 4px 15px rgba(102,126,234,0.4)',
                      zIndex: 1,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                    }}
                  />
                </Box>

                {/* Events */}
                <Collapse in={!collapsedDecades[decade]}>
                  {decadeGroups[decade].map((event, eventIndex) => {
                    const isLeft = !isMobile && eventIndex % 2 === 0;
                    const isPriority = event.priority >= 5;
                    
                    return (
                      <Box
                        key={eventIndex}
                        style={{
                          display: 'flex',
                          justifyContent: isMobile ? 'flex-start' : (isLeft ? 'flex-start' : 'flex-end'),
                          marginBottom: isMobile ? '20px' : '30px',
                          position: 'relative',
                          paddingLeft: isMobile ? '50px' : (isLeft ? '0' : '50%'),
                          paddingRight: isMobile ? '0' : (isLeft ? '50%' : '0'),
                          animation: 'fadeIn 0.5s ease'
                        }}
                      >
                        {/* Timeline Dot */}
                        <Box
                          style={{
                            position: 'absolute',
                            left: isMobile ? '10px' : (isLeft ? 'calc(50% - 18px)' : 'calc(50% - 18px)'),
                            top: '20px',
                            width: isPriority ? '36px' : '30px',
                            height: isPriority ? '36px' : '30px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${event.color} 0%, ${event.color}dd 100%)`,
                            border: isPriority ? '4px solid #ffd700' : '3px solid rgba(255, 255, 255, 0.9)',
                            boxShadow: isPriority ? '0 0 20px rgba(255, 215, 0, 0.5)' : '0 2px 10px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            transition: 'transform 0.3s ease'
                          }}
                        >
                          {React.cloneElement(event.icon, { 
                            style: { fontSize: isPriority ? '18px' : '14px', color: '#fff' } 
                          })}
                        </Box>

                        {/* Event Card */}
                        <Card
                          onClick={() => handlePersonClick(event.person)}
                          style={{
                            width: isMobile ? '100%' : 'calc(50% - 50px)',
                            background: isPriority ? 
                              'linear-gradient(135deg, rgba(255, 254, 240, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)' : 
                              'rgba(255, 255, 255, 0.98)',
                            borderRadius: '16px',
                            boxShadow: isPriority ? '0 8px 30px rgba(255, 215, 0, 0.3)' : '0 4px 20px rgba(0,0,0,0.12)',
                            cursor: (event.person && event.person._id && onPersonClick) ? 'pointer' : 'default',
                            transition: 'all 0.3s ease',
                            marginLeft: isMobile ? '0' : (isLeft ? '0' : '50px'),
                            marginRight: isMobile ? '0' : (isLeft ? '50px' : '0'),
                            border: isPriority ? '3px solid #ffd700' : '1px solid rgba(200, 200, 200, 0.3)',
                            backdropFilter: 'blur(10px)',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px) scale(1.01)';
                            e.currentTarget.style.boxShadow = isPriority ? 
                              '0 12px 40px rgba(255, 215, 0, 0.4)' : '0 8px 30px rgba(0,0,0,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            e.currentTarget.style.boxShadow = isPriority ? 
                              '0 8px 30px rgba(255, 215, 0, 0.3)' : '0 4px 20px rgba(0,0,0,0.12)';
                          }}
                        >
                          {/* Priority Banner */}
                          {isPriority && (
                            <Box style={{
                              background: 'linear-gradient(90deg, #ffd700 0%, #ffed4e 100%)',
                              padding: '4px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <StarIcon style={{ fontSize: '14px', color: '#333' }} />
                              <Typography variant="caption" style={{ fontWeight: 'bold', color: '#333' }}>
                                KEY MILESTONE
                              </Typography>
                            </Box>
                          )}
                          
                          <CardContent style={{ padding: isMobile ? '16px' : '20px' }}>
                            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                              <Box style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <Chip
                                  label={event.category}
                                  size="small"
                                  style={{
                                    background: `${event.color}25`,
                                    color: event.color,
                                    fontWeight: 'bold',
                                    fontSize: '10px',
                                    height: '22px',
                                    border: `1px solid ${event.color}50`,
                                    textTransform: 'capitalize'
                                  }}
                                />
                                {event.person.generation > 0 && (
                                  <Chip
                                    label={getGenerationLabel(event.person.generation)}
                                    size="small"
                                    style={{
                                      background: getGenerationColor(event.person.generation),
                                      color: '#333',
                                      fontWeight: 'bold',
                                      fontSize: '10px',
                                      height: '22px',
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                style={{
                                  color: event.color,
                                  fontWeight: 'bold',
                                  fontSize: isMobile ? '11px' : '13px',
                                  background: `${event.color}15`,
                                  padding: '2px 8px',
                                  borderRadius: '4px'
                                }}
                              >
                                {getDisplayDate(event)}
                              </Typography>
                            </Box>

                            <Typography
                              variant="body1"
                              style={{
                                fontWeight: '700',
                                fontSize: isMobile ? '15px' : '17px',
                                marginBottom: '8px',
                                color: '#222',
                                lineHeight: 1.3
                              }}
                            >
                              {event.title}
                            </Typography>

                            {event.description && (
                              <Typography
                                variant="body2"
                                style={{
                                  color: '#555',
                                  fontSize: isMobile ? '13px' : '14px',
                                  marginBottom: '10px',
                                  lineHeight: '1.6',
                                }}
                              >
                                {event.description}
                              </Typography>
                            )}

                            {event.details && (
                              <Typography
                                variant="caption"
                                style={{
                                  color: '#777',
                                  fontSize: isMobile ? '11px' : '12px',
                                  display: 'block',
                                  marginBottom: '12px',
                                  fontStyle: 'italic',
                                  background: 'rgba(0,0,0,0.03)',
                                  padding: '8px 10px',
                                  borderRadius: '6px',
                                  borderLeft: `3px solid ${event.color}`
                                }}
                              >
                                📝 {event.details}
                              </Typography>
                            )}

                            <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                              <Avatar
                                style={{
                                  width: isMobile ? '40px' : '44px',
                                  height: isMobile ? '40px' : '44px',
                                  background: `linear-gradient(135deg, ${event.color} 0%, ${event.color}bb 100%)`,
                                  fontSize: isMobile ? '16px' : '18px',
                                  fontWeight: 'bold',
                                  border: '3px solid rgba(255, 255, 255, 0.9)',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}
                              >
                                {event.person?.name?.charAt(0) || 'M'}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontWeight: '700',
                                    fontSize: isMobile ? '14px' : '15px',
                                    color: '#222',
                                  }}
                                >
                                  {event.person?.name || 'Family Member'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  style={{
                                    color: '#666',
                                    fontSize: isMobile ? '11px' : '12px',
                                  }}
                                >
                                  {event.person?.role || 'Family Member'}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            ))}

            {filteredEvents.length === 0 && (
              <Box style={{ textAlign: 'center', padding: '60px 20px' }}>
                <Box style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '16px',
                  padding: '40px',
                  maxWidth: '400px',
                  margin: '0 auto'
                }}>
                  <Typography variant="h6" style={{ color: '#333', marginBottom: '8px' }}>
                    No events found
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Try adjusting your search or filters
                  </Typography>
                  {(searchTerm || selectedCategories.length > 0) && (
                    <Chip
                      label="Clear Filters"
                      onClick={() => { setSearchTerm(''); setSelectedCategories([]); }}
                      style={{ marginTop: '16px', cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box style={{ textAlign: 'center', marginTop: '60px', padding: '20px' }}>
            <Box style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <Typography variant="body1" style={{ fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                🇰🇪 The Mwendwa Kitavi Family Legacy
              </Typography>
              <Typography variant="body2" style={{ color: '#666' }}>
                Kitui, Kenya • Over a century of public service
              </Typography>
              <Typography variant="caption" style={{ display: 'block', marginTop: '12px', color: '#999' }}>
                Timeline contains {stats.totalEvents} events spanning {stats.timeSpan}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TimelinePage;